import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ShieldAlert } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { DataTable } from './DataTable';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface DeploymentRiskAssessmentProps {
  catalogTasks: any[];
  incidents: any[];
}

export function DeploymentRiskAssessment({ catalogTasks, incidents }: DeploymentRiskAssessmentProps) {
  const riskAssessmentData = React.useMemo(() => {
    // Helper function to extract API names
    const extractApiName = (text: string): string | null => {
      if (!text) return null;
      
      const apiRegex = /\b([a-z0-9-]+[-]?api)\b/i;
      const systemRegex = /\b([a-z0-9-]+[-](?:sys|prc|exp))\b/i;
      
      const apiMatch = text.match(apiRegex);
      if (apiMatch) return apiMatch[0];
      
      const systemMatch = text.match(systemRegex);
      if (systemMatch) return systemMatch[0];
      
      return null;
    };
    
    // Find pending/scheduled deployments
    const pendingDeployments = catalogTasks.filter(task => {
      const status = (task.state || task.status || "").toLowerCase();
      const description = (task.short_description || task.description || "").toLowerCase();
      
      return (status.includes("scheduled") || status.includes("pending") || status.includes("open")) && 
             (description.includes("deploy") || description.includes("release"));
    });
    
    // Analyze risk for each deployment
    return pendingDeployments.map(deployment => {
      const description = deployment.short_description || deployment.description || "";
      const apiName = extractApiName(description) || "Unknown System";
      
      // Count recent incidents for this API
      const recentIncidents = incidents.filter(incident => {
        const incidentDescription = incident.short_description || incident.description || "";
        const incidentApiName = extractApiName(incidentDescription);
        
        return incidentApiName === apiName;
      }).length;
      
      // Check for recent deployments of the same system
      const recentDeployments = catalogTasks.filter(task => {
        if (task.number === deployment.number) return false;
        
        const taskDescription = task.short_description || task.description || "";
        const taskApiName = extractApiName(taskDescription);
        
        return taskApiName === apiName && 
               (taskDescription.toLowerCase().includes("deploy") || 
               taskDescription.toLowerCase().includes("release"));
      }).length;
      
      // Calculate risk score (0-100)
      let riskScore = 0;
      riskScore += recentIncidents * 10; // Each recent incident adds 10 points
      riskScore += recentDeployments * 5; // Each recent deployment adds 5 points
      
      // Description length/complexity factor
      riskScore += Math.min(20, description.length / 20);
      
      // Is it a weekend deployment?
      const deploymentDate = new Date(deployment.scheduled_date || deployment.created || "");
      if (!isNaN(deploymentDate.getTime())) {
        const day = deploymentDate.getDay();
        if (day === 0 || day === 6) { // Sunday or Saturday
          riskScore += 15; // Weekend deployments are riskier
        }
        
        // Time of day factor (deployments at night or early morning are riskier)
        const hour = deploymentDate.getHours();
        if (hour < 7 || hour > 18) {
          riskScore += 10;
        }
      }
      
      // Cap the score at 100
      riskScore = Math.min(100, Math.max(0, riskScore));
      
      return {
        taskNumber: deployment.number || "Unknown",
        apiName,
        description: deployment.short_description || deployment.description,
        riskScore: Math.round(riskScore),
        riskLevel: riskScore >= 70 ? "High" : riskScore >= 40 ? "Medium" : "Low"
      };
    }).sort((a, b) => b.riskScore - a.riskScore);
  }, [catalogTasks, incidents]);

  const columns = [
    {
      key: "taskNumber",
      header: "Task #",
      cell: (item: any) => item.taskNumber,
    },
    {
      key: "apiName",
      header: "API/System",
      cell: (item: any) => (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger>
              <Badge variant="outline">
                {item.apiName}
              </Badge>
            </TooltipTrigger>
            {item.apiName === "Unknown System" && (
              <TooltipContent>
                <p className="max-w-[300px]">{item.description}</p>
              </TooltipContent>
            )}
          </Tooltip>
        </TooltipProvider>
      ),
    },
    {
      key: "riskLevel",
      header: "Risk Level",
      cell: (item: any) => {
        let badgeVariant: "default" | "secondary" | "destructive" | "outline" = "outline";
        
        if (item.riskLevel === "High") {
          badgeVariant = "destructive";
        } else if (item.riskLevel === "Medium") {
          badgeVariant = "default";
        } else {
          badgeVariant = "secondary";
        }
        
        return (
          <Badge variant={badgeVariant}>
            {item.riskLevel}
          </Badge>
        );
      },
    },
    {
      key: "riskScore",
      header: "Risk Score",
      cell: (item: any) => {
        return (
          <div className="w-full bg-gray-200 rounded-full h-2.5">
            <div 
              className={`h-2.5 rounded-full ${
                item.riskScore >= 70 ? "bg-red-500" : 
                item.riskScore >= 40 ? "bg-yellow-500" : 
                "bg-green-500"
              }`} 
              style={{ width: `${item.riskScore}%` }}
            ></div>
          </div>
        );
      },
    },
  ];

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <ShieldAlert className="h-5 w-5" />
          Deployment Risk Assessment
        </CardTitle>
        <Badge variant="outline">
          {riskAssessmentData.length} pending deployments
        </Badge>
      </CardHeader>
      <CardContent>
        {riskAssessmentData.length > 0 ? (
          <DataTable 
            data={riskAssessmentData} 
            columns={columns} 
          />
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            No pending deployments found
          </div>
        )}
      </CardContent>
    </Card>
  );
}
